import { BusinessBusiness, BusinessSearchRequest, BusinessSearchFilters } from "../types";

export function createBusinessSearchRequest(
  rawQuery: string,
  locationText: string,
  latitude: number | null = null,
  longitude: number | null = null,
  radiusMeters: number = 40233.6,
  filters: BusinessSearchFilters = {},
  includeAdultOrientedBusinesses: boolean = false,
  language: string = "en",
  region: string = "US"
): BusinessSearchRequest {
  const normalizedQuery = rawQuery.trim().toLowerCase();
  const requestId = `req_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`;

  return {
    requestId,
    rawQuery: rawQuery.trim(),
    normalizedQuery,
    locationText: locationText.trim(),
    latitude,
    longitude,
    radiusMeters,
    filters,
    includeAdultOrientedBusinesses,
    language,
    region,
    parserVersion: "v2.4_immutable",
    relevanceEngineVersion: "v3.1_domain_locked",
    createdAt: Date.now()
  };
}

export interface BusinessTypeDefinition {
  id: string;
  label: string;
  aliases: string[];
  parentCategory: string;
  relatedTypes: string[];
  excludedMeanings?: string[];
  adultOriented?: boolean;
  modifier?: string;
  headNoun?: string;
  lockedPhrase?: boolean;
  broadParentCategories?: string[];
}

export interface RecognizedQueryTerm {
  type:
    | "business_type"
    | "service"
    | "specialty"
    | "insurance"
    | "payment"
    | "language"
    | "accessibility"
    | "hours"
    | "ownership"
    | "audience"
    | "keyword";
  canonicalId: string | null;
  displayLabel: string;
  originalText: string;
  confidence: number;
  tokenStart: number;
  tokenEnd: number;
  modifier?: string;
  headNoun?: string;
  lockedPhrase?: boolean;
  adultOriented?: boolean;
}

export type PhraseSpecificity =
  | "exact_compound_type"
  | "category_with_modifier"
  | "broad_category"
  | "custom_keywords";

export interface ParsedBusinessQuery {
  rawQuery: string;
  normalizedQuery: string;

  // Primary concepts
  businessTypes: string[];
  requiredBusinessTypes: string[];
  specialties: string[];
  services: string[];
  products: string[];

  // Specific requirement attributes
  insurancePrograms: string[];
  paymentMethods: string[];
  languages: string[];
  accessibilityFeatures: string[];
  operatingHours: string[];

  // Demographics and business attributes
  audienceOrCustomerTypes: string[];
  ownershipAttributes: string[];
  certifications: string[];

  // Syntax terms
  requiredTerms: string[];
  preferredTerms: string[];
  excludedTerms: string[];

  // Recognized terms breakdown
  recognizedTerms: RecognizedQueryTerm[];

  // Derived chips & metrics
  parsedChips: Array<{
    id: string;
    label: string;
    category: string;
    term: string;
    originalText: string;
    confidence: number;
  }>;
  isMultiKeyword: boolean;
  confidence: number;
  recognizedTokenCoverage: number;
  alwaysRunOriginalFullQuerySearch: boolean;

  // Compound phrase locking & specificity
  lockedPhrases: string[];
  phraseSpecificity: PhraseSpecificity;
  allowHeadNounFallback: boolean;
  requireStrongCategoryMatch: boolean;
  hasAdultType: boolean;
}

export type QueryMatchLevel = "verified_exact" | "strong_match" | "possible_match" | "irrelevant";

export interface CandidateValidationResult {
  matchLevel: QueryMatchLevel;
  score: number; // 0 - 100
  isRelevant: boolean;
  rejectionReason?: string;
  matchedConcepts: string[];
  exactPhraseMatch: boolean;
  businessTypeMatch: boolean;
  providerCategoryMatch: boolean;
  nameMatch: boolean;
  websiteEvidenceMatch: boolean;
}

export type RequirementVerificationStatus =
  | "verified_match"
  | "likely_match"
  | "possible_match"
  | "not_confirmed"
  | "conflicting";

export interface RequirementMatchEvidence {
  term: string;
  category: string;
  status: RequirementVerificationStatus;
  evidenceText: string | null;
  sourceType: "official_website" | "structured_data" | "provider_directory" | "verified_tag" | "unverified_text";
}

export interface QueryRelevanceResult {
  score: number; // 0 - 100
  primaryTypeMatched: boolean;
  matchedKeywords: string[];
  requirementEvidence: RequirementMatchEvidence[];
  tags: string[];
  matchExplanation: string;
  isUnconfirmedRequirementMatch: boolean;
  validationResult?: CandidateValidationResult;
}

/**
 * Structured Canonical Business-Type Taxonomy
 */
export const BUSINESS_TYPE_TAXONOMY: BusinessTypeDefinition[] = [
  // Senior Care
  {
    id: "assisted_living",
    label: "Assisted Living",
    aliases: [
      "assisted living",
      "assisted living facility",
      "assisted living community",
      "senior assisted living",
      "residential assisted living",
      "senior living",
      "assisted liveing",
      "asisted living",
      "assited living",
      "assisted livng"
    ],
    parentCategory: "Senior Care",
    relatedTypes: ["nursing_home", "memory_care", "retirement_community", "senior_care"],
    excludedMeanings: ["assisted"]
  },
  {
    id: "nursing_home",
    label: "Nursing Home",
    aliases: [
      "nursing home",
      "nursing facility",
      "skilled nursing",
      "skilled nursing facility",
      "nursing care",
      "nursing center",
      "convalescent home"
    ],
    parentCategory: "Senior Care",
    relatedTypes: ["assisted_living", "memory_care", "long_term_care"]
  },
  {
    id: "memory_care",
    label: "Memory Care",
    aliases: [
      "memory care",
      "memory care facility",
      "dementia care",
      "alzheimers care",
      "alzheimer care",
      "memory care community"
    ],
    parentCategory: "Senior Care",
    relatedTypes: ["assisted_living", "nursing_home"]
  },
  {
    id: "retirement_community",
    label: "Retirement Community",
    aliases: [
      "retirement community",
      "retirement home",
      "senior village",
      "senior living community",
      "55+ community",
      "active adult community"
    ],
    parentCategory: "Senior Care",
    relatedTypes: ["assisted_living", "independent_senior_living"]
  },
  {
    id: "independent_senior_living",
    label: "Independent Senior Living",
    aliases: [
      "independent senior living",
      "independent living",
      "senior apartments",
      "independent living facility"
    ],
    parentCategory: "Senior Care",
    relatedTypes: ["retirement_community", "assisted_living"]
  },
  {
    id: "senior_care",
    label: "Senior Care",
    aliases: [
      "senior care",
      "elder care",
      "elderly care",
      "eldercare",
      "senior services"
    ],
    parentCategory: "Senior Care",
    relatedTypes: ["assisted_living", "home_health_care", "nursing_home"]
  },
  {
    id: "adult_day_care",
    label: "Adult Day Care",
    aliases: [
      "adult day care",
      "adult day service",
      "adult daycare",
      "adult day care center"
    ],
    parentCategory: "Senior Care",
    relatedTypes: ["senior_care", "respite_care"]
  },
  {
    id: "home_health_care",
    label: "Home Health Care",
    aliases: [
      "home health care",
      "home healthcare",
      "senior home care",
      "in home care",
      "home care assistance",
      "home care service",
      "home health agency"
    ],
    parentCategory: "Senior Care",
    relatedTypes: ["senior_care", "hospice"]
  },
  {
    id: "hospice",
    label: "Hospice Care",
    aliases: [
      "hospice",
      "hospice care",
      "palliative care",
      "hospice service"
    ],
    parentCategory: "Senior Care",
    relatedTypes: ["home_health_care", "long_term_care"]
  },
  {
    id: "skilled_nursing_facility",
    label: "Skilled Nursing Facility",
    aliases: [
      "skilled nursing facility",
      "snf",
      "skilled nursing center"
    ],
    parentCategory: "Senior Care",
    relatedTypes: ["nursing_home", "rehabilitation_center"]
  },
  {
    id: "rehabilitation_center",
    label: "Rehabilitation Center",
    aliases: [
      "rehabilitation center",
      "rehab center",
      "physical rehab",
      "rehab facility"
    ],
    parentCategory: "Senior Care",
    relatedTypes: ["skilled_nursing_facility", "physical_therapy"]
  },
  {
    id: "long_term_care",
    label: "Long-Term Care",
    aliases: [
      "long term care",
      "long-term care",
      "ltc facility"
    ],
    parentCategory: "Senior Care",
    relatedTypes: ["nursing_home", "assisted_living"]
  },
  {
    id: "respite_care",
    label: "Respite Care",
    aliases: [
      "respite care",
      "respite care service"
    ],
    parentCategory: "Senior Care",
    relatedTypes: ["senior_care", "assisted_living"]
  },

  // Medical & Healthcare
  {
    id: "primary_care",
    label: "Primary Care",
    aliases: [
      "primary care",
      "primary care physician",
      "primary care doctor",
      "primary care clinic",
      "pcp"
    ],
    parentCategory: "Medical & Healthcare",
    relatedTypes: ["family_medicine", "medical_clinic"]
  },
  {
    id: "family_medicine",
    label: "Family Medicine",
    aliases: [
      "family medicine",
      "family doctor",
      "family practice",
      "family physician",
      "general practitioner",
      "family practitioner"
    ],
    parentCategory: "Medical & Healthcare",
    relatedTypes: ["primary_care", "medical_clinic"]
  },
  {
    id: "pediatrician",
    label: "Pediatrician",
    aliases: [
      "pediatrician",
      "pediatrics",
      "pediatric doctor",
      "children's doctor",
      "childrens doctor",
      "pediatric clinic",
      "childrens clinic",
      "pediatrition",
      "peditrician",
      "pedatrician"
    ],
    parentCategory: "Medical & Healthcare",
    relatedTypes: ["primary_care", "family_medicine"]
  },
  {
    id: "urgent_care",
    label: "Urgent Care",
    aliases: [
      "urgent care",
      "urgent care clinic",
      "walk in clinic",
      "walk-in clinic",
      "express care",
      "immediate care"
    ],
    parentCategory: "Medical & Healthcare",
    relatedTypes: ["medical_clinic", "primary_care"]
  },
  {
    id: "medical_clinic",
    label: "Medical Clinic",
    aliases: [
      "medical clinic",
      "doctor's office",
      "doctors office",
      "medical center",
      "physician office",
      "health clinic"
    ],
    parentCategory: "Medical & Healthcare",
    relatedTypes: ["primary_care", "urgent_care"]
  },
  {
    id: "mental_health_clinic",
    label: "Mental Health Clinic",
    aliases: [
      "mental health clinic",
      "behavioral health",
      "mental health center",
      "psychiatric clinic",
      "counseling center",
      "psychotherapy clinic"
    ],
    parentCategory: "Medical & Healthcare",
    relatedTypes: ["addiction_treatment"]
  },
  {
    id: "physical_therapy",
    label: "Physical Therapy",
    aliases: [
      "physical therapy",
      "pt clinic",
      "physiotherapy",
      "physical therapist",
      "physiotherapist"
    ],
    parentCategory: "Medical & Healthcare",
    relatedTypes: ["occupational_therapy", "rehabilitation_center"]
  },
  {
    id: "occupational_therapy",
    label: "Occupational Therapy",
    aliases: [
      "occupational therapy",
      "ot clinic",
      "occupational therapist"
    ],
    parentCategory: "Medical & Healthcare",
    relatedTypes: ["physical_therapy"]
  },
  {
    id: "women_health_clinic",
    label: "Women's Health Clinic",
    aliases: [
      "women's health clinic",
      "womens health clinic",
      "obgyn clinic",
      "gynecology",
      "obstetrics and gynecology",
      "women health"
    ],
    parentCategory: "Medical & Healthcare",
    relatedTypes: ["medical_clinic"]
  },
  {
    id: "medical_laboratory",
    label: "Medical Laboratory",
    aliases: [
      "medical laboratory",
      "med lab",
      "labcorp",
      "quest diagnostics",
      "blood lab",
      "diagnostic lab",
      "phlebotomy lab"
    ],
    parentCategory: "Medical & Healthcare",
    relatedTypes: ["diagnostic_imaging"]
  },
  {
    id: "diagnostic_imaging",
    label: "Diagnostic Imaging",
    aliases: [
      "diagnostic imaging",
      "mri center",
      "x-ray clinic",
      "imaging center",
      "radiology center",
      "ultrasound clinic"
    ],
    parentCategory: "Medical & Healthcare",
    relatedTypes: ["medical_laboratory"]
  },
  {
    id: "addiction_treatment",
    label: "Addiction Treatment",
    aliases: [
      "addiction treatment",
      "substance abuse clinic",
      "detox center",
      "recovery center",
      "rehab center"
    ],
    parentCategory: "Medical & Healthcare",
    relatedTypes: ["mental_health_clinic"]
  },
  {
    id: "dental_clinic",
    label: "Dental Clinic",
    aliases: [
      "dentist",
      "dental clinic",
      "dental office",
      "dental care",
      "orthodontics",
      "dentistry",
      "family dentist"
    ],
    parentCategory: "Medical & Healthcare",
    relatedTypes: ["pediatric_dentistry"]
  },
  {
    id: "pediatric_dentistry",
    label: "Pediatric Dentistry",
    aliases: [
      "pediatric dentistry",
      "pediatric dentist",
      "childrens dentist",
      "children's dentist"
    ],
    parentCategory: "Medical & Healthcare",
    relatedTypes: ["dental_clinic"]
  },
  {
    id: "chiropractor",
    label: "Chiropractor",
    aliases: [
      "chiropractor",
      "chiropractic clinic",
      "spine care",
      "chiropraktor",
      "chiropractic center"
    ],
    parentCategory: "Medical & Healthcare",
    relatedTypes: ["physical_therapy"]
  },
  {
    id: "dermatologist",
    label: "Dermatologist",
    aliases: [
      "dermatologist",
      "dermatology clinic",
      "skin clinic",
      "dermatologst"
    ],
    parentCategory: "Medical & Healthcare",
    relatedTypes: ["medical_clinic"]
  },
  {
    id: "optometrist",
    label: "Optometrist",
    aliases: [
      "optometrist",
      "eye doctor",
      "vision center",
      "eye care",
      "ophthalmologist"
    ],
    parentCategory: "Medical & Healthcare",
    relatedTypes: ["medical_clinic"]
  },
  {
    id: "veterinarian",
    label: "Veterinarian",
    aliases: [
      "veterinarian",
      "vet clinic",
      "animal hospital",
      "pet clinic",
      "vet doctor",
      "veternarian",
      "vetrinary"
    ],
    parentCategory: "Medical & Healthcare",
    relatedTypes: []
  },

  // Construction, Home Services & Restoration
  {
    id: "water_damage_restoration",
    label: "Water Damage Restoration",
    aliases: [
      "water damage restoration",
      "water damage",
      "flood cleanup",
      "water restoration",
      "disaster cleanup",
      "water mitigation"
    ],
    parentCategory: "Construction & Home Services",
    relatedTypes: ["mold_remediation", "restoration_company"]
  },
  {
    id: "mold_remediation",
    label: "Mold Remediation",
    aliases: [
      "mold remediation",
      "mold removal",
      "mold cleanup",
      "mold inspection"
    ],
    parentCategory: "Construction & Home Services",
    relatedTypes: ["water_damage_restoration", "restoration_company"]
  },
  {
    id: "restoration_company",
    label: "Restoration Company",
    aliases: [
      "restoration company",
      "disaster restoration",
      "fire damage restoration",
      "property restoration"
    ],
    parentCategory: "Construction & Home Services",
    relatedTypes: ["water_damage_restoration", "mold_remediation"]
  },
  {
    id: "general_contractor",
    label: "General Contractor",
    aliases: [
      "general contractor",
      "home contractor",
      "remodeling contractor",
      "builder",
      "construction company"
    ],
    parentCategory: "Construction & Home Services",
    relatedTypes: []
  },
  {
    id: "electrician",
    label: "Electrician",
    aliases: [
      "electrician",
      "electrical contractor",
      "wiring repair",
      "electric service",
      "electrican",
      "electricion"
    ],
    parentCategory: "Construction & Home Services",
    relatedTypes: []
  },
  {
    id: "plumber",
    label: "Plumber",
    aliases: [
      "plumber",
      "plumbing service",
      "drain cleaning",
      "pipe repair",
      "water heater repair",
      "plumming",
      "plumbering"
    ],
    parentCategory: "Construction & Home Services",
    relatedTypes: []
  },
  {
    id: "hvac",
    label: "HVAC",
    aliases: [
      "hvac",
      "heating and cooling",
      "air conditioning repair",
      "ac repair",
      "furnace service",
      "air conditioning contractor"
    ],
    parentCategory: "Construction & Home Services",
    relatedTypes: []
  },
  {
    id: "auto_repair",
    label: "Auto Repair",
    aliases: [
      "auto repair",
      "car repair",
      "mechanic shop",
      "auto shop",
      "brake repair",
      "car mechanic",
      "automotive service",
      "mecanic",
      "machanic"
    ],
    parentCategory: "Automotive & Transportation",
    relatedTypes: []
  },
  {
    id: "cleaning_service",
    label: "Cleaning Service",
    aliases: [
      "cleaning service",
      "house cleaning",
      "commercial cleaning",
      "maid service",
      "janitorial service"
    ],
    parentCategory: "Construction & Home Services",
    relatedTypes: []
  },
  {
    id: "pest_control",
    label: "Pest Control",
    aliases: [
      "pest control",
      "exterminator",
      "bug control",
      "termite control"
    ],
    parentCategory: "Construction & Home Services",
    relatedTypes: []
  },
  {
    id: "locksmith",
    label: "Locksmith",
    aliases: [
      "locksmith",
      "key duplication",
      "emergency locksmith"
    ],
    parentCategory: "Construction & Home Services",
    relatedTypes: []
  },
  {
    id: "handyman",
    label: "Handyman",
    aliases: [
      "handyman",
      "home repair service",
      "handyman service"
    ],
    parentCategory: "Construction & Home Services",
    relatedTypes: []
  },

  // Professional Services
  {
    id: "real_estate_agency",
    label: "Real Estate Agency",
    aliases: [
      "real estate agency",
      "realty",
      "real estate broker",
      "realtor office",
      "real estate company"
    ],
    parentCategory: "Professional Services",
    relatedTypes: ["property_management"]
  },
  {
    id: "property_management",
    label: "Property Management",
    aliases: [
      "property management",
      "property manager",
      "rental management"
    ],
    parentCategory: "Professional Services",
    relatedTypes: ["real_estate_agency"]
  },
  {
    id: "staffing_agency",
    label: "Staffing Agency",
    aliases: [
      "staffing agency",
      "employment agency",
      "temp agency",
      "job agency"
    ],
    parentCategory: "Professional Services",
    relatedTypes: ["recruiting_agency"]
  },
  {
    id: "recruiting_agency",
    label: "Recruiting Agency",
    aliases: [
      "recruiting agency",
      "executive search",
      "headhunter"
    ],
    parentCategory: "Professional Services",
    relatedTypes: ["staffing_agency"]
  },
  {
    id: "tax_preparation",
    label: "Tax Preparation",
    aliases: [
      "tax preparation",
      "cpa office",
      "tax service",
      "accounting firm",
      "accountant",
      "accountent"
    ],
    parentCategory: "Professional Services",
    relatedTypes: []
  },
  {
    id: "insurance_agency",
    label: "Insurance Agency",
    aliases: [
      "insurance agency",
      "insurance broker",
      "auto insurance agency"
    ],
    parentCategory: "Professional Services",
    relatedTypes: []
  },

  // Retail, Food & Hospitality
  {
    id: "coffee_shop",
    label: "Coffee Shop",
    aliases: [
      "coffee shop",
      "cafe",
      "espresso bar",
      "coffee house"
    ],
    parentCategory: "Food & Hospitality",
    relatedTypes: ["bakery"]
  },
  {
    id: "beauty_supply_store",
    label: "Beauty Supply Store",
    aliases: [
      "beauty supply store",
      "beauty supply",
      "cosmetics store"
    ],
    parentCategory: "Retail",
    relatedTypes: []
  },
  {
    id: "hair_salon",
    label: "Hair Salon",
    aliases: [
      "hair salon",
      "barber shop",
      "hair studio",
      "hairdresser"
    ],
    parentCategory: "Fitness, Beauty & Personal Care",
    relatedTypes: []
  },
  {
    id: "bakery",
    label: "Bakery",
    aliases: [
      "bakery",
      "pastry shop"
    ],
    parentCategory: "Food & Hospitality",
    relatedTypes: ["coffee_shop"]
  },
  {
    id: "grocery_store",
    label: "Grocery Store",
    aliases: [
      "grocery store",
      "supermarket",
      "food market"
    ],
    parentCategory: "Retail",
    relatedTypes: []
  },
  {
    id: "restaurant",
    label: "Restaurant",
    aliases: [
      "restaurant",
      "resturant",
      "restraunt",
      "dining"
    ],
    parentCategory: "Food & Hospitality",
    relatedTypes: []
  },
  {
    id: "pharmacy",
    label: "Pharmacy",
    aliases: [
      "pharmacy",
      "drugstore",
      "chemist",
      "pharamcy",
      "farmacy"
    ],
    parentCategory: "Medical & Healthcare",
    relatedTypes: []
  },
  {
    id: "daycare",
    label: "Daycare",
    aliases: [
      "daycare",
      "child care",
      "childcare",
      "preschool",
      "early learning center"
    ],
    parentCategory: "Education & Childcare",
    relatedTypes: []
  },

  // Adult-Oriented Businesses
  {
    id: "strip_club",
    label: "Strip Club",
    aliases: [
      "strip club",
      "stripclub",
      "gentlemans club",
      "gentlemens club",
      "gentlemen's club",
      "adult entertainment club",
      "burlesque club",
      "exotic dance club",
      "topless club",
      "strip lounge"
    ],
    parentCategory: "Adult Entertainment",
    relatedTypes: ["adult_entertainment", "burlesque_club", "night_club"],
    adultOriented: true,
    modifier: "Strip",
    headNoun: "Club"
  },
  {
    id: "adult_entertainment",
    label: "Adult Entertainment Venue",
    aliases: [
      "adult entertainment",
      "adult entertainment venue",
      "adult theater",
      "adult cinema",
      "adult lounge",
      "adult cabaret"
    ],
    parentCategory: "Adult Entertainment",
    relatedTypes: ["strip_club", "adult_store"],
    adultOriented: true
  },
  {
    id: "adult_store",
    label: "Adult Store",
    aliases: [
      "adult store",
      "adult boutique",
      "sex shop",
      "adult novelty store",
      "erotic boutique"
    ],
    parentCategory: "Adult Entertainment",
    relatedTypes: ["adult_entertainment"],
    adultOriented: true
  },
  {
    id: "burlesque_club",
    label: "Burlesque Club",
    aliases: [
      "burlesque club",
      "burlesque show",
      "burlesque hall"
    ],
    parentCategory: "Adult Entertainment",
    relatedTypes: ["strip_club", "night_club"],
    adultOriented: true,
    modifier: "Burlesque",
    headNoun: "Club"
  },

  // Fast Food & Dining / Cuisines
  {
    id: "fast_food",
    label: "Fast Food",
    aliases: [
      "fast food",
      "fast food restaurant",
      "drive thru restaurant",
      "quick service restaurant"
    ],
    parentCategory: "Food & Hospitality",
    relatedTypes: ["restaurant"],
    modifier: "Fast",
    headNoun: "Food"
  },
  {
    id: "mexican_restaurant",
    label: "Mexican Restaurant",
    aliases: [
      "mexican restaurant",
      "mexican food",
      "taco shop",
      "taqueria",
      "taco bell",
      "mexican grill"
    ],
    parentCategory: "Food & Hospitality",
    relatedTypes: ["restaurant"],
    modifier: "Mexican",
    headNoun: "Restaurant"
  },
  {
    id: "thai_restaurant",
    label: "Thai Restaurant",
    aliases: [
      "thai restaurant",
      "thai food",
      "thai eatery"
    ],
    parentCategory: "Food & Hospitality",
    relatedTypes: ["restaurant"],
    modifier: "Thai",
    headNoun: "Restaurant"
  },
  {
    id: "chinese_restaurant",
    label: "Chinese Restaurant",
    aliases: [
      "chinese restaurant",
      "chinese food",
      "dim sum"
    ],
    parentCategory: "Food & Hospitality",
    relatedTypes: ["restaurant"],
    modifier: "Chinese",
    headNoun: "Restaurant"
  },
  {
    id: "italian_restaurant",
    label: "Italian Restaurant",
    aliases: [
      "italian restaurant",
      "italian food",
      "pizzeria",
      "pizza shop"
    ],
    parentCategory: "Food & Hospitality",
    relatedTypes: ["restaurant"],
    modifier: "Italian",
    headNoun: "Restaurant"
  },
  {
    id: "sushi_bar",
    label: "Sushi Bar",
    aliases: [
      "sushi bar",
      "sushi restaurant",
      "japanese restaurant"
    ],
    parentCategory: "Food & Hospitality",
    relatedTypes: ["restaurant"],
    modifier: "Sushi",
    headNoun: "Bar"
  },
  {
    id: "sports_bar",
    label: "Sports Bar",
    aliases: [
      "sports bar",
      "sports pub",
      "sports grill",
      "sports lounge"
    ],
    parentCategory: "Food & Hospitality",
    relatedTypes: ["night_club", "restaurant"],
    modifier: "Sports",
    headNoun: "Bar"
  },
  {
    id: "night_club",
    label: "Night Club",
    aliases: [
      "night club",
      "nightclub",
      "dance club",
      "lounge club",
      "dance hall"
    ],
    parentCategory: "Entertainment & Nightlife",
    relatedTypes: ["sports_bar", "strip_club"],
    modifier: "Night",
    headNoun: "Club"
  },

  // Additional Services & Retail
  {
    id: "used_furniture_store",
    label: "Used Furniture Store",
    aliases: [
      "used furniture store",
      "thrift store",
      "consignment shop",
      "second hand furniture"
    ],
    parentCategory: "Retail",
    relatedTypes: [],
    modifier: "Used Furniture",
    headNoun: "Store"
  },
  {
    id: "health_insurance_agency",
    label: "Health Insurance Agency",
    aliases: [
      "health insurance agency",
      "health insurance",
      "health plan broker",
      "medical insurance broker"
    ],
    parentCategory: "Professional Services",
    relatedTypes: ["insurance_agency"]
  },
  {
    id: "emergency_dentist",
    label: "Emergency Dentist",
    aliases: [
      "emergency dentist",
      "emergency dental care",
      "24 hour dentist"
    ],
    parentCategory: "Medical & Healthcare",
    relatedTypes: ["dental_clinic"],
    modifier: "Emergency",
    headNoun: "Dentist"
  },
  {
    id: "law_firm",
    label: "Law Firm",
    aliases: [
      "law firm",
      "lawyer",
      "attorney",
      "legal services",
      "attorney at law"
    ],
    parentCategory: "Professional Services",
    relatedTypes: []
  }
];

// Backwards-compatible synonyms object generated from taxonomy
export const BUSINESS_SEARCH_SYNONYMS: Record<string, string[]> = BUSINESS_TYPE_TAXONOMY.reduce(
  (acc, item) => {
    acc[item.id] = item.aliases;
    return acc;
  },
  {} as Record<string, string[]>
);

// Common typo corrections mapping
export const TYPO_CORRECTIONS: Record<string, string> = {
  pediatrition: "pediatrician",
  peditrician: "pediatrician",
  pedatrician: "pediatrician",
  resturant: "restaurant",
  restraunt: "restaurant",
  pharamcy: "pharmacy",
  farmacy: "pharmacy",
  electrican: "electrician",
  electricion: "electrician",
  plumming: "plumbing",
  plumbering: "plumber",
  atteney: "attorney",
  lawywer: "lawyer",
  veternarian: "veterinarian",
  vetrinary: "veterinarian",
  mecanic: "mechanic",
  machanic: "mechanic",
  chiropraktor: "chiropractor",
  dermatologst: "dermatologist",
  accountent: "accountant",
  tenncare: "tenncare",
  tennecare: "tenncare",
  tencare: "tenncare",
  medicaid: "medicaid",
  medicare: "medicare",
  compny: "company",
  cleaner: "cleaning",
  asisted: "assisted",
  assited: "assisted"
};

// Recognized specific attribute categories
const KNOWN_INSURANCE = [
  "tenncare",
  "tennecare",
  "tencare",
  "medicaid",
  "medicare",
  "bluecross",
  "bcbs",
  "aetna",
  "cigna",
  "humana",
  "unitedhealthcare",
  "tricare",
  "tri care",
  "sliding scale",
  "uninsured",
  "cash only",
  "payment plan",
  "financing"
];

const KNOWN_LANGUAGES = [
  "spanish speaking",
  "spanish-speaking",
  "spanish",
  "bilingual",
  "asl",
  "american sign language",
  "french",
  "arabic",
  "vietnamese",
  "chinese",
  "mandarin",
  "russian",
  "translation available"
];

const KNOWN_ACCESSIBILITY = [
  "wheelchair accessible",
  "wheelchair-accessible",
  "wheelchair",
  "accessible entrance",
  "accessible parking",
  "accessible restroom",
  "elevator",
  "hearing assistance",
  "handicap accessible"
];

const KNOWN_HOURS = [
  "24 hour",
  "24-hour",
  "24/7",
  "open now",
  "weekend",
  "saturday",
  "sunday",
  "evening",
  "same day",
  "walk ins",
  "walk-ins",
  "emergency"
];

const KNOWN_OWNERSHIP = [
  "women owned",
  "women-owned",
  "woman owned",
  "veteran owned",
  "veteran-owned",
  "family owned",
  "family-owned",
  "minority owned",
  "minority-owned",
  "black owned",
  "black-owned",
  "locally owned",
  "locally-owned",
  "nonprofit",
  "non-profit"
];

const KNOWN_SERVICES = [
  "memory care",
  "dementia care",
  "water damage",
  "mold remediation",
  "disaster cleanup",
  "pediatrics",
  "pediatric",
  "children",
  "family law",
  "criminal defense",
  "personal injury",
  "cosmetic dentistry",
  "teeth whitening",
  "emergency service",
  "commercial cleaning",
  "residential",
  "delivery",
  "catering",
  "drive thru",
  "drive-thru",
  "outdoor seating",
  "patio",
  "pet friendly",
  "takeout",
  "small business"
];

const KNOWN_AUDIENCE = [
  "veterans",
  "veteran",
  "seniors",
  "senior",
  "students",
  "low income",
  "families"
];

const STOP_WORDS = new Set([
  "and",
  "or",
  "the",
  "a",
  "an",
  "in",
  "on",
  "at",
  "by",
  "for",
  "with",
  "about",
  "of",
  "to",
  "near",
  "me"
]);

/**
 * Helper to cleanly remove a recognized term or phrase from a raw query string.
 */
export function removeTermFromQuery(rawQuery: string, termToRemove: string): string {
  if (!rawQuery || !termToRemove) return rawQuery;
  const escaped = termToRemove.replace(/[-/\\^$*+?.()|[\]{}]/g, "\\$&");
  const regex = new RegExp(`\\b${escaped}\\b`, "gi");
  const result = rawQuery.replace(regex, "").replace(/\s+/g, " ").trim();
  return result;
}

/**
 * Normalizes query string for phrase comparison
 */
function normalizePhrase(str: string): string {
  return str
    .toLowerCase()
    .replace(/['"’]/g, "")
    .replace(/[-_]/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

/**
 * Searches a token array using longest-phrase-first matching (5-word down to 1-word).
 */
export function findRecognizedTerms(rawQuery: string): {
  terms: RecognizedQueryTerm[];
  remainingKeywords: string[];
  cleanQuery: string;
  consumed: boolean[];
  tokens: string[];
} {
  if (!rawQuery || !rawQuery.trim()) {
    return { terms: [], remainingKeywords: [], cleanQuery: "", consumed: [], tokens: [] };
  }

  // Tokenize while tracking original substrings
  const tokens = rawQuery.trim().split(/\s+/).filter(Boolean);
  const consumed = new Array(tokens.length).fill(false);
  const recognizedTerms: RecognizedQueryTerm[] = [];

  // Try longest phrases first: 5, 4, 3, 2, 1
  for (let phraseLen = Math.min(5, tokens.length); phraseLen >= 1; phraseLen--) {
    for (let i = 0; i <= tokens.length - phraseLen; i++) {
      // If any token in this range is already consumed, skip
      let rangeAlreadyConsumed = false;
      for (let k = i; k < i + phraseLen; k++) {
        if (consumed[k]) {
          rangeAlreadyConsumed = true;
          break;
        }
      }
      if (rangeAlreadyConsumed) continue;

      const sliceTokens = tokens.slice(i, i + phraseLen);
      const originalSubstr = sliceTokens.join(" ");
      const norm = normalizePhrase(originalSubstr);

      // Check typo correction for single-token phrases if needed
      let normWithTypo = norm;
      if (phraseLen === 1 && TYPO_CORRECTIONS[norm]) {
        normWithTypo = TYPO_CORRECTIONS[norm];
      }

      // 1. Business Type Taxonomy Match
      let matchedTypeDef: BusinessTypeDefinition | null = null;
      let matchConfidence = 1.0;

      for (const def of BUSINESS_TYPE_TAXONOMY) {
        const canonicalNorm = normalizePhrase(def.label);
        const idNorm = normalizePhrase(def.id);

        if (norm === canonicalNorm || normWithTypo === canonicalNorm || norm === idNorm) {
          matchedTypeDef = def;
          matchConfidence = norm === canonicalNorm ? 1.0 : 0.85;
          break;
        }

        const aliasMatch = def.aliases.some(
          alias => normalizePhrase(alias) === norm || normalizePhrase(alias) === normWithTypo
        );
        if (aliasMatch) {
          matchedTypeDef = def;
          matchConfidence = 0.95;
          break;
        }
      }

      // Check excluded standalone meanings (e.g., "assisted" alone should not trigger "assisted_living")
      if (matchedTypeDef && matchedTypeDef.excludedMeanings) {
        if (matchedTypeDef.excludedMeanings.includes(norm)) {
          matchedTypeDef = null;
        }
      }

      if (matchedTypeDef) {
        const hasPrimaryTypeAlready = recognizedTerms.some(t => t.type === "business_type");
        const termType = hasPrimaryTypeAlready ? "service" : "business_type";
        const isCompound = phraseLen > 1 || matchedTypeDef.lockedPhrase === true || Boolean(matchedTypeDef.headNoun && matchedTypeDef.modifier) || matchedTypeDef.label.includes(" ") || matchedTypeDef.id.includes("_");

        recognizedTerms.push({
          type: termType,
          canonicalId: matchedTypeDef.id,
          displayLabel: matchedTypeDef.label,
          originalText: originalSubstr,
          confidence: matchConfidence,
          tokenStart: i,
          tokenEnd: i + phraseLen,
          modifier: matchedTypeDef.modifier,
          headNoun: matchedTypeDef.headNoun,
          lockedPhrase: isCompound,
          adultOriented: Boolean(matchedTypeDef.adultOriented)
        });
        for (let k = i; k < i + phraseLen; k++) consumed[k] = true;
        continue;
      }

      // 2. Insurance Programs Match
      const matchedInsurance = KNOWN_INSURANCE.find(
        ins => normalizePhrase(ins) === norm || normalizePhrase(ins) === normWithTypo
      );
      if (matchedInsurance) {
        const canonicalInsLabel =
          matchedInsurance === "tenncare" || matchedInsurance === "tennecare" || matchedInsurance === "tencare"
            ? "TennCare"
            : matchedInsurance.charAt(0).toUpperCase() + matchedInsurance.slice(1);

        recognizedTerms.push({
          type: "insurance",
          canonicalId: matchedInsurance.includes("ten") ? "tenncare" : matchedInsurance,
          displayLabel: canonicalInsLabel,
          originalText: originalSubstr,
          confidence: 1.0,
          tokenStart: i,
          tokenEnd: i + phraseLen
        });
        for (let k = i; k < i + phraseLen; k++) consumed[k] = true;
        continue;
      }

      // 3. Services / Specialties Match
      const matchedService = KNOWN_SERVICES.find(
        srv => normalizePhrase(srv) === norm || normalizePhrase(srv) === normWithTypo
      );
      if (matchedService) {
        const displayLabel = matchedService
          .split(" ")
          .map(w => w.charAt(0).toUpperCase() + w.slice(1))
          .join(" ");

        recognizedTerms.push({
          type: "service",
          canonicalId: matchedService.replace(/\s+/g, "_"),
          displayLabel,
          originalText: originalSubstr,
          confidence: 1.0,
          tokenStart: i,
          tokenEnd: i + phraseLen
        });
        for (let k = i; k < i + phraseLen; k++) consumed[k] = true;
        continue;
      }

      // 4. Languages Match
      const matchedLanguage = KNOWN_LANGUAGES.find(
        lang => normalizePhrase(lang) === norm || normalizePhrase(lang) === normWithTypo
      );
      if (matchedLanguage) {
        const displayLabel = matchedLanguage.includes("spanish")
          ? "Spanish Speaking"
          : matchedLanguage.charAt(0).toUpperCase() + matchedLanguage.slice(1);

        recognizedTerms.push({
          type: "language",
          canonicalId: matchedLanguage.replace(/\s+/g, "_"),
          displayLabel,
          originalText: originalSubstr,
          confidence: 1.0,
          tokenStart: i,
          tokenEnd: i + phraseLen
        });
        for (let k = i; k < i + phraseLen; k++) consumed[k] = true;
        continue;
      }

      // 5. Accessibility Match
      const matchedAcc = KNOWN_ACCESSIBILITY.find(
        acc => normalizePhrase(acc) === norm || normalizePhrase(acc) === normWithTypo
      );
      if (matchedAcc) {
        recognizedTerms.push({
          type: "accessibility",
          canonicalId: "wheelchair_accessible",
          displayLabel: "Wheelchair Accessible",
          originalText: originalSubstr,
          confidence: 1.0,
          tokenStart: i,
          tokenEnd: i + phraseLen
        });
        for (let k = i; k < i + phraseLen; k++) consumed[k] = true;
        continue;
      }

      // 6. Hours Match
      const matchedHours = KNOWN_HOURS.find(
        hrs => normalizePhrase(hrs) === norm || normalizePhrase(hrs) === normWithTypo
      );
      if (matchedHours) {
        const displayLabel = matchedHours.includes("24")
          ? "Open 24 Hours"
          : matchedHours.charAt(0).toUpperCase() + matchedHours.slice(1);

        recognizedTerms.push({
          type: "hours",
          canonicalId: matchedHours.replace(/\s+/g, "_"),
          displayLabel,
          originalText: originalSubstr,
          confidence: 1.0,
          tokenStart: i,
          tokenEnd: i + phraseLen
        });
        for (let k = i; k < i + phraseLen; k++) consumed[k] = true;
        continue;
      }

      // 7. Ownership Attributes Match
      const matchedOwnership = KNOWN_OWNERSHIP.find(
        own => normalizePhrase(own) === norm || normalizePhrase(own) === normWithTypo
      );
      if (matchedOwnership) {
        const displayLabel = matchedOwnership
          .split(/[\s-]+/)
          .map(w => w.charAt(0).toUpperCase() + w.slice(1))
          .join(" ");

        recognizedTerms.push({
          type: "ownership",
          canonicalId: matchedOwnership.replace(/[\s-]+/g, "_"),
          displayLabel,
          originalText: originalSubstr,
          confidence: 1.0,
          tokenStart: i,
          tokenEnd: i + phraseLen
        });
        for (let k = i; k < i + phraseLen; k++) consumed[k] = true;
        continue;
      }

      // 8. Audience Match
      const matchedAudience = KNOWN_AUDIENCE.find(
        aud => normalizePhrase(aud) === norm || normalizePhrase(aud) === normWithTypo
      );
      if (matchedAudience) {
        recognizedTerms.push({
          type: "audience",
          canonicalId: matchedAudience,
          displayLabel: matchedAudience.charAt(0).toUpperCase() + matchedAudience.slice(1),
          originalText: originalSubstr,
          confidence: 1.0,
          tokenStart: i,
          tokenEnd: i + phraseLen
        });
        for (let k = i; k < i + phraseLen; k++) consumed[k] = true;
        continue;
      }
    }
  }

  // Collect unconsumed tokens as remaining search keywords
  const remainingKeywords: string[] = [];
  for (let k = 0; k < tokens.length; k++) {
    if (!consumed[k]) {
      const w = tokens[k];
      const lower = w.toLowerCase().replace(/[^a-z0-9]/gi, "");
      if (lower && !STOP_WORDS.has(lower)) {
        remainingKeywords.push(w);
      }
    }
  }

  return {
    terms: recognizedTerms,
    remainingKeywords,
    cleanQuery: tokens.join(" "),
    consumed,
    tokens
  };
}

/**
 * Parses a raw business search query into structured semantic concepts using longest phrase matching.
 */
export function parseBusinessQuery(rawQuery: string): ParsedBusinessQuery {
  if (!rawQuery || !rawQuery.trim()) {
    return {
      rawQuery: "",
      normalizedQuery: "",
      businessTypes: [],
      requiredBusinessTypes: [],
      specialties: [],
      services: [],
      products: [],
      insurancePrograms: [],
      paymentMethods: [],
      languages: [],
      accessibilityFeatures: [],
      operatingHours: [],
      audienceOrCustomerTypes: [],
      ownershipAttributes: [],
      certifications: [],
      requiredTerms: [],
      preferredTerms: [],
      excludedTerms: [],
      recognizedTerms: [],
      parsedChips: [],
      isMultiKeyword: false,
      confidence: 0,
      recognizedTokenCoverage: 0,
      alwaysRunOriginalFullQuerySearch: true,
      lockedPhrases: [],
      phraseSpecificity: "custom_keywords",
      allowHeadNounFallback: true,
      requireStrongCategoryMatch: false,
      hasAdultType: false
    };
  }

  const rawTrimmed = rawQuery.trim();
  let normalized = rawTrimmed;

  const requiredTerms: string[] = [];
  const excludedTerms: string[] = [];

  // Extract quoted phrases
  normalized = normalized.replace(/"([^"]+)"/g, (_, phrase) => {
    requiredTerms.push(phrase.trim());
    return "";
  });

  // Extract excluded terms (-term)
  normalized = normalized.replace(/\b-([a-zA-Z0-9]+)\b/g, (_, term) => {
    excludedTerms.push(term.trim());
    return "";
  });

  // Run longest-phrase term recognition
  const { terms, remainingKeywords, consumed, tokens } = findRecognizedTerms(normalized);

  const businessTypes: string[] = [];
  const requiredBusinessTypes: string[] = [];
  const specialties: string[] = [];
  const services: string[] = [];
  const insurancePrograms: string[] = [];
  const paymentMethods: string[] = [];
  const languages: string[] = [];
  const accessibilityFeatures: string[] = [];
  const operatingHours: string[] = [];
  const audienceOrCustomerTypes: string[] = [];
  const ownershipAttributes: string[] = [];

  const parsedChips: Array<{
    id: string;
    label: string;
    category: string;
    term: string;
    originalText: string;
    confidence: number;
  }> = [];

  terms.forEach(term => {
    const chipId = `${term.type}-${term.canonicalId || term.displayLabel.toLowerCase().replace(/\s+/g, "_")}`;

    if (term.type === "business_type") {
      if (term.canonicalId && !businessTypes.includes(term.canonicalId)) {
        businessTypes.push(term.canonicalId);
        requiredBusinessTypes.push(term.canonicalId);
      }
      parsedChips.push({
        id: chipId,
        label: term.displayLabel,
        category: "BUSINESS TYPE",
        term: term.canonicalId || term.displayLabel,
        originalText: term.originalText,
        confidence: term.confidence
      });
    } else if (term.type === "service" || term.type === "specialty") {
      services.push(term.displayLabel);
      parsedChips.push({
        id: chipId,
        label: term.displayLabel,
        category: "SERVICE",
        term: term.displayLabel,
        originalText: term.originalText,
        confidence: term.confidence
      });
    } else if (term.type === "insurance") {
      insurancePrograms.push(term.canonicalId || term.displayLabel);
      parsedChips.push({
        id: chipId,
        label: term.displayLabel,
        category: "INSURANCE",
        term: term.canonicalId || term.displayLabel,
        originalText: term.originalText,
        confidence: term.confidence
      });
    } else if (term.type === "language") {
      languages.push(term.displayLabel);
      parsedChips.push({
        id: chipId,
        label: term.displayLabel,
        category: "LANGUAGE",
        term: term.displayLabel,
        originalText: term.originalText,
        confidence: term.confidence
      });
    } else if (term.type === "accessibility") {
      accessibilityFeatures.push(term.displayLabel);
      parsedChips.push({
        id: chipId,
        label: term.displayLabel,
        category: "ACCESSIBILITY",
        term: term.displayLabel,
        originalText: term.originalText,
        confidence: term.confidence
      });
    } else if (term.type === "hours") {
      operatingHours.push(term.displayLabel);
      parsedChips.push({
        id: chipId,
        label: term.displayLabel,
        category: "HOURS",
        term: term.displayLabel,
        originalText: term.originalText,
        confidence: term.confidence
      });
    } else if (term.type === "ownership") {
      ownershipAttributes.push(term.displayLabel);
      parsedChips.push({
        id: chipId,
        label: term.displayLabel,
        category: "OWNERSHIP",
        term: term.displayLabel,
        originalText: term.originalText,
        confidence: term.confidence
      });
    } else if (term.type === "audience") {
      audienceOrCustomerTypes.push(term.displayLabel);
      parsedChips.push({
        id: chipId,
        label: term.displayLabel,
        category: "AUDIENCE",
        term: term.displayLabel,
        originalText: term.originalText,
        confidence: term.confidence
      });
    }
  });

  // Calculate token coverage
  const totalMeaningfulTokens = tokens.filter(t => !STOP_WORDS.has(t.toLowerCase())).length;
  const consumedTokenCount = consumed ? consumed.filter(Boolean).length : 0;
  const recognizedTokenCoverage = totalMeaningfulTokens > 0 ? Math.min(1.0, consumedTokenCount / totalMeaningfulTokens) : 1.0;

  // Handle remaining unconsumed tokens: if no taxonomy match, display full SEARCH PHRASE chip
  if (parsedChips.length === 0 && rawTrimmed.length > 0) {
    parsedChips.push({
      id: `search-phrase-${rawTrimmed.toLowerCase().replace(/\s+/g, "_")}`,
      label: rawTrimmed,
      category: "SEARCH PHRASE",
      term: rawTrimmed,
      originalText: rawTrimmed,
      confidence: 0.85
    });
  } else if (remainingKeywords.length > 0 && recognizedTokenCoverage < 0.8) {
    const unconsumedPhrase = remainingKeywords.join(" ");
    parsedChips.push({
      id: `search-phrase-${unconsumedPhrase.toLowerCase().replace(/\s+/g, "_")}`,
      label: unconsumedPhrase,
      category: "SEARCH PHRASE",
      term: unconsumedPhrase,
      originalText: unconsumedPhrase,
      confidence: 0.75
    });
  }

  const overallConfidence =
    parsedChips.length > 0
      ? Math.min(...parsedChips.map(c => c.confidence))
      : 0.5;

  const lockedPhrases: string[] = [];
  terms.forEach(t => {
    if (t.lockedPhrase || t.displayLabel.includes(" ") || t.originalText.includes(" ")) {
      if (!lockedPhrases.includes(t.displayLabel)) {
        lockedPhrases.push(t.displayLabel);
      }
    }
  });

  const hasAdultType =
    terms.some(t => t.adultOriented || t.canonicalId === "strip_club" || t.canonicalId === "adult_entertainment" || t.canonicalId === "adult_store" || t.canonicalId === "burlesque_club") ||
    businessTypes.some(b => {
      const def = BUSINESS_TYPE_TAXONOMY.find(d => d.id === b);
      return def?.adultOriented || b === "strip_club" || b === "adult_entertainment" || b === "adult_store" || b === "burlesque_club";
    }) ||
    rawQuery.toLowerCase().includes("strip club") ||
    rawQuery.toLowerCase().includes("gentlemen's club") ||
    rawQuery.toLowerCase().includes("adult entertainment");

  let phraseSpecificity: PhraseSpecificity = "custom_keywords";
  if (
    lockedPhrases.length > 0 ||
    businessTypes.some(b => {
      const def = BUSINESS_TYPE_TAXONOMY.find(d => d.id === b);
      return def ? (def.label.includes(" ") || def.aliases.some(a => a.includes(" "))) : false;
    })
  ) {
    phraseSpecificity = "exact_compound_type";
  } else if (terms.some(t => t.modifier && t.headNoun)) {
    phraseSpecificity = "category_with_modifier";
  } else if (businessTypes.length > 0) {
    phraseSpecificity = "broad_category";
  }

  const allowHeadNounFallback = phraseSpecificity !== "exact_compound_type";
  const requireStrongCategoryMatch =
    phraseSpecificity === "exact_compound_type" || phraseSpecificity === "category_with_modifier";

  return {
    rawQuery,
    normalizedQuery: rawQuery.toLowerCase().trim(),
    businessTypes,
    requiredBusinessTypes,
    specialties,
    services,
    products: [],
    insurancePrograms,
    paymentMethods,
    languages,
    accessibilityFeatures,
    operatingHours,
    audienceOrCustomerTypes,
    ownershipAttributes,
    certifications: [],
    requiredTerms,
    preferredTerms: remainingKeywords,
    excludedTerms,
    recognizedTerms: terms,
    parsedChips,
    isMultiKeyword: parsedChips.length > 1,
    confidence: overallConfidence,
    recognizedTokenCoverage,
    alwaysRunOriginalFullQuerySearch: recognizedTokenCoverage < 0.8 || true,
    lockedPhrases,
    phraseSpecificity,
    allowHeadNounFallback,
    requireStrongCategoryMatch,
    hasAdultType
  };
}

/**
 * Evaluates whether a candidate business meets query relevance gates using a tiered evidence system.
 */
export function validateCandidateAgainstQuery(
  business: BusinessBusiness,
  parsedQuery: ParsedBusinessQuery,
  includeAdultOriented: boolean = false
): CandidateValidationResult {
  const matchedConcepts: string[] = [];
  const nameLower = (business.name || "").toLowerCase();
  const catLower = (
    (business.category || "") +
    " " +
    ((business as any).providerCategory || "") +
    " " +
    (Array.isArray((business as any).providerTypes) ? (business as any).providerTypes.join(" ") : "")
  ).toLowerCase();
  const descLower = (business.description || "").toLowerCase();
  const notesLower = ((business as any).notes || "").toLowerCase();
  const tagsLower = Array.isArray((business as any).tags)
    ? (business as any).tags.join(" ").toLowerCase()
    : "";
  const amenitiesLower = Array.isArray((business as any).amenities)
    ? (business as any).amenities.join(" ").toLowerCase()
    : "";

  const combinedText = `${nameLower} ${catLower} ${descLower} ${notesLower} ${tagsLower} ${amenitiesLower}`.trim();

  const isExactRawQueryProviderResult = Boolean(
    (business as any).isExactRawQueryProviderResult ||
    (business as any).retrievedViaPathA ||
    (Array.isArray((business as any).retrievedVia) && (business as any).retrievedVia.includes("exact_raw_query"))
  );

  const providerCategoryMatch = catLower.length > 0;
  const nameMatch = nameLower.length > 0;
  const websiteEvidenceMatch = Boolean((business as any).website);

  // Check if business is adult-oriented
  const isBusinessAdult = Boolean(
    (business as any).isAdultOriented ||
    (business as any).adultOriented ||
    catLower.includes("strip club") ||
    catLower.includes("adult entertainment") ||
    catLower.includes("gentlemen's club") ||
    catLower.includes("gentlemans club") ||
    catLower.includes("cabaret") ||
    catLower.includes("exotic dance") ||
    catLower.includes("strip lounge") ||
    nameLower.includes("strip club") ||
    nameLower.includes("gentlemen's club") ||
    nameLower.includes("gentlemans club") ||
    nameLower.includes("spearmint rhino") ||
    nameLower.includes("rick's cabaret")
  );

  // Check if query requested adult-oriented business
  const isQueryAdult = Boolean(
    parsedQuery.hasAdultType ||
    parsedQuery.recognizedTerms.some(
      t => t.adultOriented || t.canonicalId === "strip_club" || t.canonicalId === "adult_entertainment" || t.canonicalId === "adult_store" || t.canonicalId === "burlesque_club"
    ) ||
    parsedQuery.businessTypes.some(b => b === "strip_club" || b === "adult_entertainment" || b === "adult_store" || b === "burlesque_club") ||
    parsedQuery.rawQuery.toLowerCase().includes("strip club") ||
    parsedQuery.rawQuery.toLowerCase().includes("gentlemen's club") ||
    parsedQuery.rawQuery.toLowerCase().includes("adult entertainment")
  );

  // 1. Filter out adult queries/businesses if feature is turned off or query didn't request them
  if (isQueryAdult && !includeAdultOriented) {
    return {
      matchLevel: "irrelevant",
      score: 0,
      isRelevant: false,
      rejectionReason: "Adult-oriented businesses are currently excluded by your search settings. Enable 'Include adult-oriented businesses' in filters to search this category.",
      matchedConcepts: [],
      exactPhraseMatch: false,
      businessTypeMatch: false,
      providerCategoryMatch: false,
      nameMatch: false,
      websiteEvidenceMatch: false
    };
  }

  if (isBusinessAdult && (!includeAdultOriented || !isQueryAdult)) {
    return {
      matchLevel: "irrelevant",
      score: 0,
      isRelevant: false,
      rejectionReason: "Adult-oriented business filtered out by adult content settings.",
      matchedConcepts: [],
      exactPhraseMatch: false,
      businessTypeMatch: false,
      providerCategoryMatch: false,
      nameMatch: false,
      websiteEvidenceMatch: false
    };
  }

  // 2. Verified Adult-Entertainment Evidence Gate (when adult content IS enabled)
  if (isQueryAdult && includeAdultOriented) {
    const hasVerifiedAdultEvidence = Boolean(
      (business as any).isAdultOriented ||
      (business as any).adultOriented ||
      (Array.isArray((business as any).providerTypes) && (business as any).providerTypes.some((t: string) =>
        ["strip_club", "adult_entertainment", "adult_store", "burlesque_club", "stripclub"].includes(t.toLowerCase())
      )) ||
      catLower.includes("strip club") ||
      catLower.includes("adult entertainment") ||
      catLower.includes("gentlemen's club") ||
      catLower.includes("gentlemans club") ||
      catLower.includes("gentlemens club") ||
      catLower.includes("adult cabaret") ||
      catLower.includes("exotic dance") ||
      catLower.includes("strip lounge") ||
      catLower.includes("adult store") ||
      catLower.includes("burlesque") ||
      nameLower.includes("strip club") ||
      nameLower.includes("gentlemen's club") ||
      nameLower.includes("gentlemans club") ||
      nameLower.includes("gentlemens club") ||
      nameLower.includes("spearmint rhino") ||
      nameLower.includes("rick's cabaret") ||
      nameLower.includes("cheetahs") ||
      nameLower.includes("vivid") ||
      descLower.includes("strip club") ||
      descLower.includes("gentlemen's club") ||
      descLower.includes("exotic dance") ||
      notesLower.includes("strip club")
    );

    if (!hasVerifiedAdultEvidence) {
      return {
        matchLevel: "irrelevant",
        score: 0,
        isRelevant: false,
        rejectionReason: `Candidate name ("${business.name}") and category ("${business.category || 'Unspecified'}") lack verified adult-entertainment evidence required for query "${parsedQuery.rawQuery}".`,
        matchedConcepts: [],
        exactPhraseMatch: false,
        businessTypeMatch: false,
        providerCategoryMatch: false,
        nameMatch: false,
        websiteEvidenceMatch: false
      };
    }
  }

  // 3. Contradictory Primary Categories Map & Domain Gates
  const FOOD_TERMS = ["pizza", "burger", "taco", "sushi", "pasta", "bakery", "coffee", "cafe", "diner", "food", "restaurant", "steakhouse", "barbecue", "bbq", "ice cream", "donut", "ramen", "thai", "chinese", "mexican", "italian", "seafood", "bistro", "grill", "hawaiian pizza", "pizzeria"];
  const MEDICAL_DENTAL_TERMS = ["dental", "dentist", "orthodontist", "teeth", "tooth", "doctor", "physician", "medical", "clinic", "hospital", "optometrist", "chiropractor", "pediatrician", "dermatologist", "veterinarian"];
  const AUTO_TERMS = ["auto repair", "car repair", "mechanic", "oil change", "brake repair", "tire shop", "car dealer", "transmission"];
  const TRADES_TERMS = ["plumber", "plumbing", "electrician", "roofing", "hvac", "air conditioning", "handyman"];

  const rawNorm = parsedQuery.rawQuery.toLowerCase();

  const isFoodQuery = FOOD_TERMS.some(t => rawNorm.includes(t)) || parsedQuery.businessTypes.some(b => ["restaurant", "pizza_shop", "fast_food", "coffee_shop", "bakery"].includes(b));
  const isMedicalDentalQuery = MEDICAL_DENTAL_TERMS.some(t => rawNorm.includes(t)) || parsedQuery.businessTypes.some(b => ["dental", "medical_clinic", "hospital", "doctor"].includes(b));
  const isAutoQuery = AUTO_TERMS.some(t => rawNorm.includes(t)) || parsedQuery.businessTypes.some(b => ["auto_repair", "car_dealer"].includes(b));
  const isTradesQuery = TRADES_TERMS.some(t => rawNorm.includes(t)) || parsedQuery.businessTypes.some(b => ["plumber", "electrician"].includes(b));

  const isMedicalDentalCandidate = MEDICAL_DENTAL_TERMS.some(t => catLower.includes(t));
  const isFoodCandidate = FOOD_TERMS.some(t => catLower.includes(t));
  const isAutoCandidate = AUTO_TERMS.some(t => catLower.includes(t));
  const isTradesCandidate = TRADES_TERMS.some(t => catLower.includes(t));

  if (isFoodQuery && isMedicalDentalCandidate) {
    return {
      matchLevel: "irrelevant",
      score: 0,
      isRelevant: false,
      rejectionReason: `Candidate category ("${business.category || 'Unspecified'}") is medical/dental, which contradicts food/dining query "${parsedQuery.rawQuery}".`,
      matchedConcepts: [],
      exactPhraseMatch: false,
      businessTypeMatch: false,
      providerCategoryMatch: false,
      nameMatch: false,
      websiteEvidenceMatch: false
    };
  }

  if (isMedicalDentalQuery && isFoodCandidate) {
    return {
      matchLevel: "irrelevant",
      score: 0,
      isRelevant: false,
      rejectionReason: `Candidate category ("${business.category || 'Unspecified'}") is food/dining, which contradicts medical/dental query "${parsedQuery.rawQuery}".`,
      matchedConcepts: [],
      exactPhraseMatch: false,
      businessTypeMatch: false,
      providerCategoryMatch: false,
      nameMatch: false,
      websiteEvidenceMatch: false
    };
  }

  if (isFoodQuery && (isAutoCandidate || isTradesCandidate)) {
    return {
      matchLevel: "irrelevant",
      score: 0,
      isRelevant: false,
      rejectionReason: `Candidate category ("${business.category || 'Unspecified'}") contradicts food/dining query "${parsedQuery.rawQuery}".`,
      matchedConcepts: [],
      exactPhraseMatch: false,
      businessTypeMatch: false,
      providerCategoryMatch: false,
      nameMatch: false,
      websiteEvidenceMatch: false
    };
  }

  if (isMedicalDentalQuery && (isAutoCandidate || isTradesCandidate)) {
    return {
      matchLevel: "irrelevant",
      score: 0,
      isRelevant: false,
      rejectionReason: `Candidate category ("${business.category || 'Unspecified'}") contradicts medical/dental query "${parsedQuery.rawQuery}".`,
      matchedConcepts: [],
      exactPhraseMatch: false,
      businessTypeMatch: false,
      providerCategoryMatch: false,
      nameMatch: false,
      websiteEvidenceMatch: false
    };
  }

  const CONTRADICTORY_CATEGORIES_MAP: Record<string, string[]> = {
    strip_club: [
      "restaurant", "diner", "steakhouse", "steak_house", "cafe", "coffee",
      "jewelry_store", "country_club", "sports_club", "fitness_center", "gym",
      "dance_school", "retail_store", "strip_mall", "shopping_center", "bakery",
      "dental", "electrician", "plumber", "auto_repair", "hardware_store", "grocery_store", "local_service"
    ],
    adult_entertainment: [
      "restaurant", "diner", "cafe", "jewelry_store", "country_club", "sports_club",
      "fitness_center", "school", "daycare", "dental", "retail_store"
    ],
    fast_food: [
      "fine_dining", "jewelry_store", "hardware_store", "dental", "auto_repair", "clothing_store"
    ],
    coffee_shop: [
      "night_club", "auto_repair", "hardware_store", "dental", "plumbing"
    ],
    country_club: [
      "night_club", "strip_club", "fast_food", "auto_repair"
    ],
    assisted_living: [
      "night_club", "bar", "restaurant", "auto_repair"
    ]
  };

  // Check Contradictions
  for (const bType of parsedQuery.businessTypes) {
    const contradictoryList = CONTRADICTORY_CATEGORIES_MAP[bType];
    if (contradictoryList) {
      const isContradictoryCategory = contradictoryList.some(
        c => catLower.includes(c.replace(/_/g, " ")) || (c === "restaurant" && catLower.includes("restaurant"))
      );

      if (isContradictoryCategory) {
        // Must have explicit positive evidence for the requested bType to overcome contradictory primary category
        const taxonomyDef = BUSINESS_TYPE_TAXONOMY.find(d => d.id === bType);
        const searchTerms = taxonomyDef
          ? [taxonomyDef.label.toLowerCase(), taxonomyDef.id.replace(/_/g, " "), ...taxonomyDef.aliases.map(a => a.toLowerCase())]
          : [bType.replace(/_/g, " ")];

        const hasExplicitPositiveMatch = searchTerms.some(
          t => nameLower.includes(t) || catLower.includes(t) || descLower.includes(t)
        );

        if (!hasExplicitPositiveMatch) {
          return {
            matchLevel: "irrelevant",
            score: 0,
            isRelevant: false,
            rejectionReason: `Candidate primary category ("${business.category || 'Unspecified'}") contradicts required business type (${bType}).`,
            matchedConcepts: [],
            exactPhraseMatch: false,
            businessTypeMatch: false,
            providerCategoryMatch: false,
            nameMatch: false,
            websiteEvidenceMatch: false
          };
        }
      }
    }
  }

  // 4. Check Business Type Taxonomy Match
  let businessTypeMatch = false;
  let taxonomyMatchLevel: QueryMatchLevel = "irrelevant";

  if (parsedQuery.businessTypes.length > 0) {
    for (const bType of parsedQuery.businessTypes) {
      const taxonomyDef = BUSINESS_TYPE_TAXONOMY.find(d => d.id === bType);

      if (taxonomyDef) {
        const canonicalNorm = taxonomyDef.label.toLowerCase();
        const searchTerms = [
          canonicalNorm,
          taxonomyDef.id.replace(/_/g, " "),
          ...taxonomyDef.aliases.map(a => a.toLowerCase())
        ];

        const exactCatOrName = searchTerms.some(
          t => catLower.includes(t) || nameLower.includes(t)
        );

        if (exactCatOrName) {
          businessTypeMatch = true;
          taxonomyMatchLevel = "verified_exact";
          matchedConcepts.push(taxonomyDef.label);
          break;
        }

        // For locked phrases, do NOT match if modifier and head noun appear in unrelated contexts (e.g. "Strip" mall + "Club" sandwich)
        if (taxonomyDef.modifier && taxonomyDef.headNoun && parsedQuery.allowHeadNounFallback) {
          const mod = taxonomyDef.modifier.toLowerCase();
          const head = taxonomyDef.headNoun.toLowerCase();
          const hasModAndHead = combinedText.includes(mod) && combinedText.includes(head);

          if (hasModAndHead) {
            businessTypeMatch = true;
            taxonomyMatchLevel = "strong_match";
            matchedConcepts.push(taxonomyDef.label);
            break;
          }
        } else if (searchTerms.some(t => combinedText.includes(t))) {
          businessTypeMatch = true;
          taxonomyMatchLevel = "strong_match";
          matchedConcepts.push(taxonomyDef.label);
          break;
        }
      } else {
        const termNorm = bType.toLowerCase().replace(/_/g, " ");
        if (catLower.includes(termNorm) || nameLower.includes(termNorm)) {
          businessTypeMatch = true;
          taxonomyMatchLevel = "verified_exact";
          matchedConcepts.push(bType);
          break;
        } else if (combinedText.includes(termNorm)) {
          businessTypeMatch = true;
          taxonomyMatchLevel = "strong_match";
          matchedConcepts.push(bType);
          break;
        }
      }
    }
  } else {
    businessTypeMatch = true;
    taxonomyMatchLevel = "possible_match";
  }

  const rawQueryLower = (parsedQuery.rawQuery || "").toLowerCase().trim();
  const containsFullQuery = rawQueryLower.length > 0 && combinedText.includes(rawQueryLower);

  // Check required terms (e.g. quoted terms)
  if (parsedQuery.requiredTerms.length > 0) {
    const allRequiredPresent = parsedQuery.requiredTerms.every(term =>
      combinedText.includes(term.toLowerCase())
    );
    if (!allRequiredPresent) {
      return {
        matchLevel: "irrelevant",
        score: 0,
        isRelevant: false,
        rejectionReason: "Candidate missing required search term(s).",
        matchedConcepts,
        exactPhraseMatch: containsFullQuery,
        businessTypeMatch,
        providerCategoryMatch,
        nameMatch,
        websiteEvidenceMatch
      };
    }
  }

  // 5. Tiered Evidence Scoring System
  let relevancePoints = 0;
  if (isExactRawQueryProviderResult) relevancePoints += 45;
  if (businessTypeMatch) relevancePoints += 35;
  if (containsFullQuery) relevancePoints += 35;

  const queryNameOverlap = rawQueryLower.length > 0 && nameLower.includes(rawQueryLower);
  if (queryNameOverlap) relevancePoints += 30;

  if (websiteEvidenceMatch && containsFullQuery) relevancePoints += 25;
  if (matchedConcepts.length > 0) relevancePoints += 20;

  const queryTokens = rawQueryLower.split(/\s+/).filter(t => t.length > 2 && !STOP_WORDS.has(t));
  const matchedTokens = queryTokens.filter(t => combinedText.includes(t));
  if (matchedTokens.length > 0 && !containsFullQuery) {
    relevancePoints += Math.min(20, matchedTokens.length * 5);
  }

  const canonicalPhraseMatch = containsFullQuery;
  const officialWebsiteConfirmation = websiteEvidenceMatch && containsFullQuery;

  // Primary match requirement for locked compound queries
  let hasPrimaryMatch = false;
  if (parsedQuery.phraseSpecificity === "exact_compound_type" || parsedQuery.requireStrongCategoryMatch) {
    hasPrimaryMatch = businessTypeMatch || canonicalPhraseMatch || queryNameOverlap;
  } else {
    hasPrimaryMatch =
      isExactRawQueryProviderResult ||
      businessTypeMatch ||
      canonicalPhraseMatch ||
      queryNameOverlap ||
      officialWebsiteConfirmation ||
      (parsedQuery.businessTypes.length === 0 && matchedTokens.length > 0);
  }

  const isRelevant = hasPrimaryMatch && relevancePoints >= 30;

  if (!isRelevant) {
    let rejectionReason = `Candidate name ("${business.name}") and category ("${business.category || 'Unspecified'}") have no relationship to query "${parsedQuery.rawQuery}".`;
    if (parsedQuery.businessTypes.length > 0 && !businessTypeMatch && !isExactRawQueryProviderResult) {
      rejectionReason = `Candidate name ("${business.name}") and category ("${business.category || 'Unspecified'}") do not match required business type (${parsedQuery.businessTypes.join(", ")}).`;
    }

    return {
      matchLevel: "irrelevant",
      score: 0,
      isRelevant: false,
      rejectionReason,
      matchedConcepts,
      exactPhraseMatch: containsFullQuery,
      businessTypeMatch,
      providerCategoryMatch,
      nameMatch,
      websiteEvidenceMatch
    };
  }

  const finalScore = Math.min(100, Math.max(30, relevancePoints));
  let matchLevel: QueryMatchLevel = "possible_match";
  if (finalScore >= 80) matchLevel = "verified_exact";
  else if (finalScore >= 50) matchLevel = "strong_match";

  return {
    matchLevel,
    score: finalScore,
    isRelevant: true,
    matchedConcepts,
    exactPhraseMatch: containsFullQuery,
    businessTypeMatch,
    providerCategoryMatch,
    nameMatch,
    websiteEvidenceMatch
  };
}

/**
 * Evaluates a business against specific requirement attributes (e.g. TennCare, 24-hour, Spanish speaking)
 */
export function verifySearchRequirements(
  business: BusinessBusiness,
  parsedQuery: ParsedBusinessQuery
): RequirementMatchEvidence[] {
  const evidences: RequirementMatchEvidence[] = [];

  const textToSearch = [
    business.name,
    business.category,
    business.description,
    (business as any).website,
    (business as any).notes,
    Array.isArray((business as any).tags) ? (business as any).tags.join(" ") : "",
    Array.isArray((business as any).features) ? (business as any).features.join(" ") : "",
    Array.isArray((business as any).amenities) ? (business as any).amenities.join(" ") : ""
  ]
    .filter(Boolean)
    .join(" ")
    .toLowerCase();

  // 1. Verify Insurance requirement
  for (const ins of parsedQuery.insurancePrograms) {
    const hasExplicitIns =
      textToSearch.includes(ins.toLowerCase()) ||
      (ins.toLowerCase().includes("tenncare") &&
        (textToSearch.includes("tenncare") || textToSearch.includes("medicaid") || textToSearch.includes("tenn care")));

    if (hasExplicitIns) {
      evidences.push({
        term: ins.toLowerCase().includes("tenncare") ? "TennCare" : ins,
        category: "Insurance",
        status: "verified_match",
        evidenceText: `Verified insurance support found in business details for ${
          ins.toLowerCase().includes("tenncare") ? "TennCare" : ins
        }.`,
        sourceType: "official_website"
      });
    } else {
      evidences.push({
        term: ins.toLowerCase().includes("tenncare") ? "TennCare" : ins,
        category: "Insurance",
        status: "not_confirmed",
        evidenceText: `${
          ins.toLowerCase().includes("tenncare") ? "TennCare" : ins
        } acceptance not explicitly confirmed on file for this provider.`,
        sourceType: "unverified_text"
      });
    }
  }

  // 2. Verify Language requirement
  for (const lang of parsedQuery.languages) {
    const hasLang =
      textToSearch.includes(lang.toLowerCase()) ||
      (lang.toLowerCase().includes("spanish") &&
        (textToSearch.includes("espanol") || textToSearch.includes("spanish") || textToSearch.includes("bilingual")));

    if (hasLang) {
      evidences.push({
        term: lang.toLowerCase().includes("spanish") ? "Spanish Speaking" : lang,
        category: "Language",
        status: "verified_match",
        evidenceText: `Verified ${lang} service availability.`,
        sourceType: "official_website"
      });
    } else {
      evidences.push({
        term: lang.toLowerCase().includes("spanish") ? "Spanish Speaking" : lang,
        category: "Language",
        status: "not_confirmed",
        evidenceText: `${lang} language availability not confirmed.`,
        sourceType: "unverified_text"
      });
    }
  }

  // 3. Verify Accessibility requirement
  for (const acc of parsedQuery.accessibilityFeatures) {
    const hasAcc =
      textToSearch.includes("wheelchair") ||
      textToSearch.includes("accessible") ||
      textToSearch.includes("handicap");

    if (hasAcc) {
      evidences.push({
        term: "Wheelchair Accessible",
        category: "Accessibility",
        status: "verified_match",
        evidenceText: "Verified wheelchair accessibility features.",
        sourceType: "structured_data"
      });
    } else {
      evidences.push({
        term: "Wheelchair Accessible",
        category: "Accessibility",
        status: "not_confirmed",
        evidenceText: "Accessibility features not explicitly confirmed.",
        sourceType: "unverified_text"
      });
    }
  }

  // 4. Verify Operating Hours requirement
  for (const hrs of parsedQuery.operatingHours) {
    const hasHrs =
      textToSearch.includes("24 hour") ||
      textToSearch.includes("24/7") ||
      textToSearch.includes("24-hour") ||
      textToSearch.includes("open 24");

    if (hasHrs) {
      evidences.push({
        term: "Open 24 Hours",
        category: "Hours",
        status: "verified_match",
        evidenceText: "Verified 24-hour service operation.",
        sourceType: "official_website"
      });
    } else {
      evidences.push({
        term: "Open 24 Hours",
        category: "Hours",
        status: "not_confirmed",
        evidenceText: "24-hour operation unconfirmed.",
        sourceType: "unverified_text"
      });
    }
  }

  return evidences;
}

/**
 * Calculates detailed query relevance score and tags for a business
 */
export function calculateQueryRelevanceScore(
  business: BusinessBusiness,
  parsedQuery: ParsedBusinessQuery,
  distanceInMiles?: number | null,
  includeAdultOriented: boolean = false
): QueryRelevanceResult {
  const validationResult = validateCandidateAgainstQuery(business, parsedQuery, includeAdultOriented);

  if (!parsedQuery.rawQuery || !parsedQuery.rawQuery.trim()) {
    return {
      score: 100,
      primaryTypeMatched: true,
      matchedKeywords: [],
      requirementEvidence: [],
      tags: [],
      matchExplanation: "General search query.",
      isUnconfirmedRequirementMatch: false,
      validationResult
    };
  }

  if (!validationResult.isRelevant) {
    return {
      score: 0,
      primaryTypeMatched: validationResult.businessTypeMatch,
      matchedKeywords: [],
      requirementEvidence: [],
      tags: [],
      matchExplanation: validationResult.rejectionReason || "Irrelevant candidate.",
      isUnconfirmedRequirementMatch: false,
      validationResult
    };
  }

  const nameLower = (business.name || "").toLowerCase();
  const catLower = (business.category || "").toLowerCase();
  const descLower = (business.description || "").toLowerCase();

  const requirementEvidences = verifySearchRequirements(business, parsedQuery);

  let primaryTypeMatched = validationResult.businessTypeMatch;
  let matchedTypeLabel = validationResult.matchedConcepts[0] || "";

  // Specific requirement score
  let verifiedReqCount = 0;
  requirementEvidences.forEach(ev => {
    if (ev.status === "verified_match") verifiedReqCount++;
  });

  const totalRequirements = requirementEvidences.length;

  const matchedKeywords: string[] = [];
  parsedQuery.preferredTerms.forEach(term => {
    const tLower = term.toLowerCase();
    if (nameLower.includes(tLower) || catLower.includes(tLower) || descLower.includes(tLower)) {
      matchedKeywords.push(term);
    }
  });

  const tags: string[] = [];
  if (primaryTypeMatched) {
    tags.push(matchedTypeLabel || business.category);
  } else if (business.category) {
    tags.push(business.category);
  }

  requirementEvidences.forEach(ev => {
    if (ev.status === "verified_match") {
      tags.push(`${ev.term} Verified`);
    } else if (ev.status === "not_confirmed") {
      tags.push(`${ev.term} Unconfirmed`);
    }
  });

  const isUnconfirmedRequirementMatch = totalRequirements > 0 && verifiedReqCount === 0;

  let matchExplanation = "";
  if (primaryTypeMatched && verifiedReqCount > 0) {
    matchExplanation = `Verified match for ${matchedTypeLabel} accepting ${requirementEvidences
      .map(e => e.term)
      .join(", ")}.`;
  } else if (primaryTypeMatched && isUnconfirmedRequirementMatch) {
    matchExplanation = `${matchedTypeLabel} category match; ${requirementEvidences
      .map(e => e.term)
      .join(", ")} acceptance unconfirmed.`;
  } else if (primaryTypeMatched) {
    matchExplanation = `Direct category match for ${matchedTypeLabel}.`;
  } else {
    matchExplanation = `Keyword relevance for "${parsedQuery.rawQuery}".`;
  }

  return {
    score: validationResult.score,
    primaryTypeMatched,
    matchedKeywords,
    requirementEvidence: requirementEvidences,
    tags,
    matchExplanation,
    isUnconfirmedRequirementMatch,
    validationResult
  };
}

/**
 * Comprehensive Category Groups for Search Dropdowns
 */
export const EXPANDED_CATEGORY_GROUPS = [
  {
    name: "Senior Care & Living",
    items: [
      "Assisted Living",
      "Nursing Home",
      "Memory Care",
      "Retirement Community",
      "Independent Senior Living",
      "Senior Care",
      "Adult Day Care",
      "Home Health Care",
      "Hospice",
      "Skilled Nursing Facility",
      "Rehabilitation Center",
      "Long-Term Care",
      "Respite Care"
    ]
  },
  {
    name: "Medical & Healthcare",
    items: [
      "Primary Care",
      "Family Medicine",
      "Pediatrician",
      "Urgent Care",
      "Medical Clinic",
      "Mental Health Clinic",
      "Physical Therapy",
      "Occupational Therapy",
      "Women’s Health Clinic",
      "Medical Laboratory",
      "Diagnostic Imaging",
      "Addiction Treatment",
      "Dental Clinic",
      "Pediatric Dentistry",
      "Chiropractor",
      "Dermatologist",
      "Optometrist",
      "Pharmacy",
      "Veterinarian"
    ]
  },
  {
    name: "Construction & Home Services",
    items: [
      "Water Damage Restoration",
      "Mold Remediation",
      "Restoration Company",
      "General Contractor",
      "Electrician",
      "Plumber",
      "HVAC",
      "Cleaning Service",
      "Pest Control",
      "Locksmith",
      "Handyman"
    ]
  },
  {
    name: "Automotive & Transportation",
    items: [
      "Auto Repair",
      "Car Dealership",
      "Tire Shop",
      "Auto Body Shop",
      "Car Wash",
      "Towing Service"
    ]
  },
  {
    name: "Professional Services",
    items: [
      "Real Estate Agency",
      "Property Management",
      "Staffing Agency",
      "Recruiting Agency",
      "Tax Preparation",
      "Insurance Agency",
      "Lawyer",
      "Accountant"
    ]
  },
  {
    name: "Retail & Shopping",
    items: [
      "Beauty Supply Store",
      "Clothing Store",
      "Shoe Store",
      "Electronics Store",
      "Furniture Store",
      "Grocery Store"
    ]
  },
  {
    name: "Food & Hospitality",
    items: [
      "Coffee Shop",
      "Bakery",
      "Restaurant",
      "Fast Food",
      "Catering",
      "Hotel"
    ]
  },
  {
    name: "Fitness & Personal Care",
    items: [
      "Gym",
      "Hair Salon",
      "Barber Shop",
      "Nail Salon",
      "Spa",
      "Massage Therapist"
    ]
  },
  {
    name: "Education & Childcare",
    items: [
      "Daycare",
      "Preschool",
      "Tutoring",
      "Private School"
    ]
  }
];
